import { Box, Link } from "@mui/material";
import { CheckoutContext } from "contexts/Checkout";
import {
  SplashPage,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext } from "react";
import { AddOnItemName } from "schemas/dashboard";
import {
  BillingCycle,
  PLAN_FAMILY,
  Plans,
  getCycleForPlan,
  getPlanForCycle,
} from "schemas/payments";

import { isB2GOPromoActive } from "constants/influencer";

import styles from "../styles";
import { useCheckout } from "../useCheckout";

const HIDDEN_SPLASH_SOURCES = [
  SplashPage.ooo,
  SplashPage.pro_bundle,
  SplashPage.va_money_back,
  SplashPage.annual,
  SplashPage.va_bundled,
];

const CYCLE_TAB: { [cycle in BillingCycle]: SubscriptionTab } = {
  [BillingCycle.monthly]: SubscriptionTab.monthly,
  [BillingCycle.yearly]: SubscriptionTab.yearly,
  [BillingCycle.quarterly]: SubscriptionTab.quarterly,
};

const BillingCycleSwitch = () => {
  const { plan, splashPageSource } = useContext(CheckoutContext);
  const { subscription } = useContext(SubscriptionContext);
  const { switchBillingCycle } = useCheckout();

  if (splashPageSource && HIDDEN_SPLASH_SOURCES.includes(splashPageSource)) {
    return null;
  }

  const isEmailOnly =
    plan === AddOnItemName.email_volume_monthly ||
    plan === AddOnItemName.email_volume_yearly;

  const family = !isEmailOnly && plan ? PLAN_FAMILY[plan as Plans] : undefined;

  if (!isEmailOnly && !family) return null;

  const activeCycle: BillingCycle =
    plan === AddOnItemName.email_volume_yearly
      ? BillingCycle.yearly
      : plan === AddOnItemName.email_volume_monthly
        ? BillingCycle.monthly
        : getCycleForPlan(plan as Plans);

  const isPaidMonthlyOrYearly =
    Boolean(subscription?.isSubscribed) &&
    [BillingCycle.monthly, BillingCycle.yearly].includes(
      getCycleForPlan(subscription?.subscription?.planName),
    );
  const showQuarterly = isB2GOPromoActive() && !isPaidMonthlyOrYearly;

  const cycles: BillingCycle[] = isEmailOnly
    ? [BillingCycle.monthly, BillingCycle.yearly]
    : [
        BillingCycle.monthly,
        BillingCycle.quarterly,
        BillingCycle.yearly,
      ].filter(
        (cycle) =>
          Boolean(getPlanForCycle(plan as Plans, cycle)) &&
          (cycle !== BillingCycle.quarterly || showQuarterly),
      );

  if (cycles.length < 2) return null;

  const renderAnnotation = (cycle: BillingCycle) => {
    if (cycle === BillingCycle.yearly) {
      return (
        <Box sx={styles.annotation}>
          <Box
            component="i"
            className="fa-regular fa-arrow-down"
            sx={{ mr: 0.5 }}
          />{" "}
          25% off
        </Box>
      );
    }
    if (cycle === BillingCycle.quarterly) {
      return (
        <Box sx={[styles.annotation, { color: "#15803D" }]}>
          <Box component="i" className="fa-solid fa-gift" sx={{ mr: 0.5 }} /> 1
          month free
        </Box>
      );
    }
    return null;
  };

  const label = (cycle: BillingCycle) => {
    if (cycle === BillingCycle.monthly) return "Monthly";
    if (cycle === BillingCycle.yearly) return "Yearly";
    return "Buy 2, Get 1";
  };

  return (
    <Box sx={styles.tabContainer}>
      {cycles.map((cycle) => (
        <Link
          key={cycle}
          sx={[styles.tab, activeCycle === cycle ? styles.active : {}]}
          onClick={() => switchBillingCycle(CYCLE_TAB[cycle])}
        >
          {label(cycle)}
          {renderAnnotation(cycle)}
        </Link>
      ))}
    </Box>
  );
};

export default BillingCycleSwitch;
